import moment from 'moment';

import { SERVER_DATE_FORMAT } from 'utils/dates';
import type { MultiFileEntry } from 'lib/atoms/file-atom';
import { formatBytes } from '../utils';
import type { AfeDetailSupportingFile, AfeHeaderDetail } from '../types';
import type {
  AfeFormValues,
  CostItem,
  SaveAfeRequest,
  SelectedEntity,
  SupportingFileAttributesEntry,
  WellType,
  WizardFileRow,
} from './types';

/** Default form values for create mode */
export const INITIAL_FORM: AfeFormValues = {
  // Step 1
  afeType: null,
  afeDate: moment().format(SERVER_DATE_FORMAT),
  // Step 2
  entities: [],
  // Step 3
  wellType: null,
  uwi: null,
  slotId: null,
  importedWellName: null,
  stateId: null,
  countyId: null,
  operator: null,
  basinName: null,
  reportedFormation: null,
  plannedLateralLength: null,
  plannedTvd: null,
  wellName: null,
  wellOperator: null,
  wellUnit: null,
  drilledLateralLength: null,
  trueVerticalDepth: null,
  // Step 4
  costItems: [
    {
      id: null,
      name: '',
      category: '',
      subCategory: '',
      cost: null,
    },
  ],
  heldFlag: null,
  // Step 5
  notes: '',
};

/**
 * Transforms TanStack Form values into the API request payload for
 * POST/PATCH /afe_headers. `newUploads` and `pendingDeletes` fold into
 * a single `supporting_files_attributes` array (completed-and-not-hidden
 * uploads → `{ attachment: signed_id }`; pending-delete ids →
 * `{ id, _destroy: true }`). The array is omitted entirely when both
 * sources are empty so single-arg callers don't ship a stray empty field.
 * @param {AfeFormValues} values - Complete form values from form.state.values.
 * @param {MultiFileEntry[]} [newUploads] - In-flight + completed uploads (default `[]`).
 * @param {Set<number>} [pendingDeletes] - Existing-file ids marked for save-time delete (default empty).
 * @return {SaveAfeRequest} The API request payload.
 */
export const buildAfePayload = (
  values: AfeFormValues,
  newUploads: MultiFileEntry[] = [],
  pendingDeletes: Set<number> = new Set()
): SaveAfeRequest => {
  const currentScenarioIds = new Set([values.scenarioId].filter(Boolean));
  const destroyedScenarios = (values.originalScenarioIds ?? [])
    .filter((id) => !currentScenarioIds.has(id))
    .map((id) => ({
      id,
      _destroy: true as const,
      name: '',
      is_active: false,
      lateral_length: null,
      tvd: null,
      afe_cost_items_attributes: [] as never[],
    }));

  const currentCostItemIds = new Set(
    values.costItems.map((item) => item.id).filter(Boolean)
  );
  const destroyedCostItems = (values.originalCostItemIds ?? [])
    .filter((id) => !currentCostItemIds.has(id))
    .map((id) => ({
      id,
      _destroy: true as const,
      name: '',
      category: '',
      sub_category: '',
      unit: 'per_well',
      cost_per_unit: 0,
    }));

  const currentEntityIds = new Set(
    values.entities.map((e) => e.afeEntityId).filter(Boolean)
  );
  const destroyedEntities = (values.originalEntityIds ?? [])
    .filter((id) => !currentEntityIds.has(id))
    .map((id) => ({
      id,
      _destroy: true as const,
      entity_id: 0,
      role: 'sender' as const,
    }));

  const supportingFilesAttributes: SupportingFileAttributesEntry[] = [
    ...newUploads
      .filter(
        (e): e is MultiFileEntry & { file: string } =>
          !e.hidden && e.status === 'complete' && !!e.file
      )
      .map((e) => ({ attachment: e.file })),
    ...Array.from(pendingDeletes).map((id) => ({
      id,
      _destroy: true as const,
    })),
  ];

  return {
    afe_header: {
      afe_type: values.afeType!,
      well_type: values.wellType!,
      slot_id: values.slotId,
      well_name:
        values.wellType === 'generic_well'
          ? values.importedWellName
          : values.wellName,
      // Only an imported well has an operator input, so only it sends one; an
      // AFE on an existing well keeps the operator it has.
      ...(values.wellType === 'generic_well'
        ? { operator: values.operator }
        : {}),
      basin_name: values.basinName,
      reported_formation: values.reportedFormation,
      afe_date: values.afeDate,
      notes: values.notes || null,
      held_flag: values.heldFlag,
      state_id: values.stateId,
      county_id: values.countyId,
      afe_scenarios_attributes: [
        {
          ...(values.scenarioId ? { id: values.scenarioId } : {}),
          name: 'Base Case',
          is_active: true,
          lateral_length: values.plannedLateralLength,
          tvd: values.plannedTvd,
          afe_cost_items_attributes: [
            ...values.costItems.map((item) => ({
              ...(item.id ? { id: item.id } : {}),
              name: item.name,
              category: item.category,
              sub_category: item.subCategory,
              unit: 'per_well',
              cost_per_unit: item.cost ?? 0,
            })),
            ...destroyedCostItems,
          ],
        },
        ...destroyedScenarios,
      ],
      afe_entities_attributes: [
        ...values.entities.map((e) => ({
          ...(e.afeEntityId ? { id: e.afeEntityId } : {}),
          entity_id: e.entityId,
          role: e.role,
        })),
        ...destroyedEntities,
      ],
      ...(supportingFilesAttributes.length > 0 && {
        supporting_files_attributes: supportingFilesAttributes,
      }),
    },
  };
};

/**
 * Transforms an AFE detail API response into TanStack Form values for edit mode.
 * Maps from the nested AfeHeaderDetail structure (wellInformation, wellLookup,
 * afeScenarios) to the flat form values shape.
 * @param {AfeHeaderDetail} afe - The AFE detail from GET /afe_headers/:id
 * @return {AfeFormValues} Form values for useForm({ defaultValues })
 */
export const afeDetailToFormValues = (afe: AfeHeaderDetail): AfeFormValues => {
  const wi = afe.wellInformation;
  const wl = afe.wellLookup;
  const scenario = afe.afeScenarios[0] ?? null;

  return {
    afeType: afe.afeType,
    afeDate: afe.afeDate ?? null,
    entities: afe.entities.map((e): SelectedEntity => ({
      afeEntityId: e.id,
      entityId: e.entityId,
      name: e.name,
      href: e.href,
      address: e.address,
      entityType: e.entityType,
      role: e.role as SelectedEntity['role'],
    })),
    wellType: afe.wellType as WellType,
    uwi: afe.slotId,
    slotId: afe.slotId,
    importedWellName: afe.wellType === 'generic_well' ? wi.wellName : null,
    stateId: afe.stateId,
    countyId: afe.countyId,
    operator: afe.operator ?? null,
    basinName: wi.basinName ?? null,
    reportedFormation: wi.reportedFormation ?? null,
    scenarioId: scenario?.id,
    plannedLateralLength: scenario?.lateralLength ?? null,
    plannedTvd: scenario?.tvd ?? null,
    wellName: wi.wellName ?? null,
    wellOperator: wl.operator ?? null,
    wellUnit: wl.unit ?? null,
    drilledLateralLength: wi.drilledLateralLength ?? null,
    trueVerticalDepth: wi.trueVerticalDepth ?? null,
    costItems: (scenario?.costItems ?? []).map((item): CostItem => ({
      id: item.id,
      name: item.name,
      category: item.category,
      subCategory: item.subCategory ?? '',
      cost: item.costPerUnit,
    })),
    heldFlag: afe.heldFlag,
    notes: afe.notes ?? '',
    originalScenarioIds: afe.afeScenarios.map((s) => s.id),
    originalCostItemIds: (scenario?.costItems ?? []).map((item) => item.id),
    originalEntityIds: afe.entities.map((e) => e.id),
  };
};

/**
 * Pure derivation of the wizard's visible-files list from its three
 * sources. Filters out existing files whose ids are in `pendingDeletes`,
 * filters out new uploads marked `hidden`, and projects both to
 * {@link WizardFileRow}. Existing rows come first; new rows appended
 * at the end. Used by `wizardVisibleFilesAtom` and exported for direct
 * unit testing.
 * @param {AfeDetailSupportingFile[]} existing - Files from the AFE detail.
 * @param {MultiFileEntry[]} newUploads - Files staged via the multi-file factory.
 * @param {Set<number>} pendingDeletes - Existing-file ids marked for save-time delete.
 * @return {WizardFileRow[]} Normalized combined list, existing-then-new.
 */
export const buildWizardVisibleFiles = (
  existing: AfeDetailSupportingFile[],
  newUploads: MultiFileEntry[],
  pendingDeletes: Set<number>
): WizardFileRow[] => {
  const visibleExisting: WizardFileRow[] = existing
    .filter((f) => !pendingDeletes.has(f.id))
    .map((f) => ({
      key: `existing-${f.id}`,
      source: 'existing',
      name: f.fileName,
      size:
        f.byteSize !== null && f.byteSize !== undefined
          ? formatBytes(f.byteSize)
          : null,
      date: f.createdAt ?? null,
      href: f.attachmentHref ?? undefined,
      existingId: f.id,
    }));

  const visibleNew: WizardFileRow[] = newUploads
    .filter((e) => !e.hidden)
    .map((e) => ({
      key: e.localId,
      source: 'new',
      name: e.name ?? '',
      size:
        e.byteSize !== null && e.byteSize !== undefined
          ? formatBytes(e.byteSize)
          : null,
      date: e.createdAt ?? null,
      status: e.status,
      localId: e.localId,
      href:
        e.status === 'complete' && e.file && e.name
          ? `/rails/active_storage/blobs/redirect/${
              e.file
            }/${encodeURIComponent(e.name)}?disposition=inline`
          : undefined,
    }));

  return [...visibleExisting, ...visibleNew];
};
